// src/components/SubscriptionRequiredModal.jsx - ENHANCED with subscription coverage details
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  SparklesIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// 2-1-26
import subscriptionService from '../services/subscriptionService';

// end 2-1-26

const SubscriptionRequiredModal = ({ 
  isOpen, 
  onClose, 
  subscriptionData,
  onWaitlistJoin,
  loading,
  email,
  eventSlug,
  onRsvpSuccess
}) => {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState(null);
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
  const [error, setError] = useState(null);
  const [modalRoot, setModalRoot] = useState(null);
  
  // Create portal root on mount
  useEffect(() => {
    let root = document.getElementById('modal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'modal-root';
      root.style.position = 'relative';
      root.style.zIndex = '9999';
      document.body.appendChild(root);
    }
    setModalRoot(root);
  }, []);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  
  if (!isOpen || !subscriptionData) return null;
  
  // 🆕 NEW: Destructure coverage-related fields
  const {
    eventTitle = 'This Event',
    eventDate = new Date().toISOString(),
    eventSlug: dataEventSlug,
    
    // 🆕 NEW: Subscription coverage fields
    subscriptionEndDate,
    daysCoverage,
    eligibilityStatus,
    hasSubscription = false,
    subscriptionExpiresBeforeEvent = false,
    blockingMessage,
    suggestedAction,
    
    // Existing fields
    paymentOptions = {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true,
      perEventPriceCents: NULL
    },
    plans = {
      monthly: { price: 210000, interval: 'month' },
      sixMonth: { price: 1134000, interval: '6_months', discount: 10 },
      yearly: { price: 2142000, interval: 'year', discount: 15 }
    },
    isFull = false,
    spotsAvailable = 5,
    waitlistCount = 0,
    capacity = 25,
    currentCount = 0
  } = subscriptionData || {};
  
  // Safe defaults for nested objects
  const origsafePaymentOptions = {
    allowSubscription: true,
    allowPerEventPayment: false,
    allowWaitlist: true,
    perEventPriceCents: null,
    ...paymentOptions
  };

  // 1-30-26 update
  const safePaymentOptions = {
    allowSubscription: true,
    allowPerEventPayment: true, // ✅ ENABLED
    allowWaitlist: false, // ✅ DISABLED - No waitlist
    perEventPriceCents: 90000, // ✅ ¥900 default
    ...paymentOptions
  };
  
  const safePlans = {
    monthly: { price: 210000, interval: 'month' },
    sixMonth: { price: 1134000, interval: '6_months', discount: 10 },
    yearly: { price: 2142000, interval: 'year', discount: 15 },
    ...plans
  };
  
  const finalEventSlug = eventSlug || dataEventSlug;
  
  // 🆕 NEW: Determine if this is a coverage issue vs no subscription
  const isCoverageIssue = subscriptionEndDate && daysCoverage !== null && daysCoverage !== undefined;
  const isBlocking = subscriptionExpiresBeforeEvent || !hasSubscription;
  const isExpiringWarning = isCoverageIssue && !subscriptionExpiresBeforeEvent && daysCoverage <= 7;
  
  console.log('🔍 Modal Debug:', {
    hasSubscriptionData: !!subscriptionData,
    eventTitle,
    eventDate,
    finalEventSlug,
    isCoverageIssue,
    isBlocking,
    isExpiringWarning,
    subscriptionEndDate,
    daysCoverage,
    eligibilityStatus,
    hasSubscription,
    subscriptionExpiresBeforeEvent,
    email,
    hasEmail: !!email,
    modalRootExists: !!modalRoot
  });
  
  // 🆕 NEW: Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  // 2-1-26
  // 🆕 NEW: Direct pay-per-event checkout — bypasses subscription page entirely
const handlePayPerEvent = async () => {
  // If no email available, fall back to subscription page where they can enter one
  if (!email) {
    navigate('/subscription');
    return;
  }

  // Validate email format
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  if (!emailRegex.test(email)) {
    setError('Please enter a valid email address');
    return;
  }

  setIsJoiningWaitlist(true); // reuse existing loading state
  setError(null);

  try {
    subscriptionService.storeCheckoutEmail(email);
    const result = await subscriptionService.createPayPerEventCheckout(email);

    if (result.success) {
      window.location.href = result.checkoutUrl; // → Stripe, done.
    } else {
      setError(result.error || 'Failed to create checkout session');
    }
  } catch (err) {
    console.error('Pay-per-event checkout error:', err);
    setError('An unexpected error occurred. Please try again.');
  } finally {
    setIsJoiningWaitlist(false);
  }
};

  // end 2-1-26
  
  const handleSubscribe = () => {
    navigate('/subscription?source=event-required');
  };
  
  const handleWaitlist = async () => {
    if (!email) {
      setError('Email address is required');
      return;
    }

    if (!finalEventSlug) {
      setError('Event information is missing');
      return;
    }

    setIsJoiningWaitlist(true);
    setError(null);
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        console.error('❌ Missing Supabase configuration');
        setError('Configuration error. Please contact support.');
        setIsJoiningWaitlist(false);
        return;
      }
      
      console.log('🎯 Joining waitlist for:', {
        email,
        eventSlug: finalEventSlug,
        eventTitle
      });
      
      const response = await fetch(`${supabaseUrl}/functions/v1/email-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          email: email,
          source: `event-${finalEventSlug}`,
          metadata: {
            event_slug: finalEventSlug,
            event_title: eventTitle,
            event_date: eventDate,
            rsvp_status_override: 'waitlisted',
            waitlist_source: 'subscription_modal',
            event_capacity: capacity,
            event_payment_enabled: true,
            event_requires_subscription: true,
            ...subscriptionData
          }
        })
      });

      const result = await response.json();
      
      console.log('✅ Waitlist response:', result);
      
      if (result.success) {
        console.log('✅ Waitlist join successful:', result);
        
        if (onRsvpSuccess) {
          onRsvpSuccess(result);
        }
        
        onClose();
        
      } else if (result.isDuplicate) {
        setError('You\'re already on the waitlist for this event!');
        setTimeout(() => {
          onClose();
        }, 2000);
        
      } else {
        console.error('❌ Waitlist join failed:', result);
        setError(result.error || 'Failed to join waitlist. Please try again.');
      }
      
    } catch (error) {
      console.error('❌ Waitlist join error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsJoiningWaitlist(false);
    }
  };
  
  // Modal content component
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-4 bg-black bg-opacity-50"
          style={{ zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`relative text-white p-6 rounded-t-2xl ${
              isBlocking 
                ? 'bg-gradient-to-r from-red-600 to-red-700' 
                : isExpiringWarning 
                ? 'bg-gradient-to-r from-yellow-600 to-orange-600'
                : 'bg-gradient-to-r from-purple-600 to-blue-600'
            }`}>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors z-10"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              
              <div className="flex items-center mb-2">
                {isCoverageIssue ? (
                  <ExclamationTriangleIcon className="w-8 h-8 mr-3" />
                ) : (
                  <SparklesIcon className="w-8 h-8 mr-3" />
                )}
                <h2 className="text-2xl font-bold">
                  {isCoverageIssue 
                    ? (isBlocking ? 'Subscription Coverage Issue' : 'Subscription Expiring Soon')
                    : 'Subscriber-Only Event'}
                </h2>
              </div>
              <p className={`${
                isBlocking ? 'text-red-100' : isExpiringWarning ? 'text-yellow-100' : 'text-purple-100'
              }`}>
                {isCoverageIssue
                  ? (blockingMessage || 'Your subscription does not cover this event date')
                  : 'This exclusive event requires an active paid subscription'}
              </p>
            </div>
            
            {/* 🆕 NEW: Subscription Coverage Details Section */}
            {isCoverageIssue && (
              <div className={`p-6 border-b ${
                isBlocking 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <h3 className={`font-bold mb-4 flex items-center text-lg ${
                  isBlocking ? 'text-red-900' : 'text-yellow-900'
                }`}>
                  <CalendarDaysIcon className="w-6 h-6 mr-2" />
                  Subscription Timeline
                </h3>
                
                {/* Date Comparison Grid */}
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 mb-1">📅 Event Date</p>
                    <p className="font-bold text-gray-900 text-lg">
                      {formatDate(eventDate)}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 mb-1">⏰ Subscription Expires</p>
                    <p className={`font-bold text-lg ${
                      daysCoverage < 0 ? 'text-red-600' : 'text-yellow-600'
                    }`}>
                      {formatDate(subscriptionEndDate)}
                    </p>
                  </div>
                </div>
                
                {/* Coverage Status Indicator */}
                <div className={`p-4 rounded-lg border-2 ${
                  daysCoverage < 0 
                    ? 'bg-red-100 border-red-300' 
                    : 'bg-yellow-100 border-yellow-300'
                }`}>
                  <p className="text-xs font-medium text-gray-600 mb-2">Coverage Status:</p>
                  {daysCoverage < 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">❌</span>
                      <p className="font-bold text-red-700 text-lg">
                        Expires {Math.abs(daysCoverage)} day{Math.abs(daysCoverage) !== 1 ? 's' : ''} BEFORE the event
                      </p>
                    </div>
                  ) : daysCoverage <= 7 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">⚠️</span>
                      <p className="font-bold text-yellow-700 text-lg">
                        Expires {daysCoverage} day{daysCoverage !== 1 ? 's' : ''} AFTER the event
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">✅</span>
                      <p className="font-bold text-green-700 text-lg">
                        Covers event with {daysCoverage} days to spare
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Suggested Action */}
                {suggestedAction && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <strong className="font-semibold">💡 Suggested Action:</strong> {suggestedAction}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Event Info */}
            <div className={`p-6 border-b ${
              isCoverageIssue 
                ? (isBlocking ? 'bg-red-50/50 border-red-100' : 'bg-yellow-50/50 border-yellow-100')
                : 'bg-purple-50 border-purple-100'
            }`}>
              <div className="flex items-start">
                <CalendarDaysIcon className={`w-6 h-6 mr-3 flex-shrink-0 mt-1 ${
                  isBlocking ? 'text-red-600' : isExpiringWarning ? 'text-yellow-600' : 'text-purple-600'
                }`} />
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{eventTitle}</h3>
                  <p className="text-gray-600">{formatDate(eventDate)}</p>
                  <div className="mt-2 flex items-center text-sm">
                    <UserGroupIcon className="w-4 h-4 text-gray-500 mr-1" />
                     {/* 2-26-26
                    <span className="text-gray-600">
                      {isFull ? 'Event Full' : `${spotsAvailable} spots available`}
                      {waitlistCount > 0 && ` • ${waitlistCount} on waitlist`}
                    </span>
                    */}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Error Message Display */}
            {error && (
              <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}
            
            {/* Options */}
            <div className="p-6 space-y-4">
              <h3 className="font-semibold text-gray-900 mb-4">
                {isCoverageIssue 
                  ? (isBlocking ? 'Extend Your Subscription:' : 'Choose Your Path:')
                  : 'Choose Your Path:'}
              </h3>
              
              {/* Option 1: Subscribe/Extend */}
              {safePaymentOptions.allowSubscription && (
                <div
                  onClick={() => setSelectedOption('subscribe')}
                  className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
                    selectedOption === 'subscribe'
                      ? (isBlocking ? 'border-red-500 bg-red-50' : 'border-purple-500 bg-purple-50')
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isBlocking 
                          ? 'bg-gradient-to-br from-red-500 to-red-600'
                          : 'bg-gradient-to-br from-purple-500 to-blue-500'
                      }`}>
                        <SparklesIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {hasSubscription ? 'Extend Your Subscription' : 'Become a Paid Member'}
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">
                        {hasSubscription 
                          ? 'Extend your subscription to cover this event and maintain access'
                          : 'Get unlimited access to all subscriber events + member benefits'}
                      </p>
                      
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            {hasSubscription ? 'Recommended' : 'Flexible Monthly'}
                          </span>

                          {/*
                          <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">
                            Save {safePlans.sixMonth?.discount || 10}%
                          </span>
                          */}

                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-lg font-bold text-gray-900">
                            ¥{((safePlans.monthly?.price || 210000) / 100).toLocaleString()}
                          </span>
                          <span className="text-sm text-gray-600">per month</span>
                        </div>
                           {/*
                        <p className="text-xs text-gray-500 mt-1">
                          ¥{Math.round((safePlans.sixMonth?.price || 1134000) / 6 / 100).toLocaleString()}/month • All events included
                        </p>
                         */}
                      </div>
                      
                      {selectedOption === 'subscribe' && (
                        <button
                          onClick={handleSubscribe}
                          disabled={loading || isJoiningWaitlist}
                          className={`w-full mt-3 text-white py-3 rounded-lg font-semibold transition-all disabled:opacity-50 ${
                            isBlocking
                              ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                              : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                          }`}
                        >
                          {hasSubscription ? 'Extend Subscription →' : 'View Membership Plans →'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
             
              {/* Option 2: Pay-Per-Event Pass */}
              {/* uncomment this to allow for pay-per-event 900 Y*/}
                {/*
              {(safePaymentOptions.allowPerEventPayment || true) && ( // ✅ Always show if perEventPriceCents exists
                <div
                  onClick={() => setSelectedOption('per-event')}
                  className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
                    selectedOption === 'per-event'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                        <CreditCardIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">
                        Buy Single Event Pass
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">
                        One-time payment for this event only (valid for 90 days)
                      </p>
                      
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">One-Time Pass</span>
                          <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold">
                            No Commitment
                          </span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-lg font-bold text-gray-900">
                            ¥900
                          </span>
                          <span className="text-sm text-gray-600">single use</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Valid for 90 days • Use on any event
                        </p>
                      </div>
                      
                      {selectedOption === 'per-event' && (
                        <button
                          onClick={handlePayPerEvent}
                          disabled={loading || isJoiningWaitlist}
                          className="w-full mt-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50"
                        >
                          {isJoiningWaitlist ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </span>
                          ) : (
                            'Buy Event Pass (¥900) →'
                          )}
                        </button>
                      )}

                    </div>
                  </div>
                </div>
              )}
              */}

              
              
              {/* Option 3: Join Waitlist */}
                {/* 
              {safePaymentOptions.allowWaitlist && (
                <div
                  onClick={() => setSelectedOption('waitlist')}
                  className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
                    selectedOption === 'waitlist'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center">
                        <ClockIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">
                        Join the Waitlist (Free)
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        We'll notify you if spots become available for non-subscribers
                      </p>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                        <p className="text-orange-800 font-medium mb-1">
                          ⚠️ Limited Availability
                        </p>
                        <p className="text-orange-700 text-xs">
                          Priority goes to paid subscribers. Waitlist spots are not guaranteed.
                          {waitlistCount > 0 && ` Currently ${waitlistCount} people on waitlist.`}
                        </p>
                      </div>
                      
                      {selectedOption === 'waitlist' && (
                        <button
                          onClick={handleWaitlist}
                          disabled={loading || isJoiningWaitlist}
                          className="w-full mt-3 bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-all disabled:opacity-50 flex items-center justify-center"
                        >
                          {isJoiningWaitlist ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Joining Waitlist...
                            </>
                          ) : (
                            'Join Free Waitlist →'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
               */}

            </div>
            
            {/* Footer */}
            <div className="p-6 bg-gray-50 rounded-b-2xl border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-600">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                <p>
                  <strong>Paid Member Subscribers</strong> get guaranteed access to all events, <strong>curated Fikas</strong>, 24-hour early booking, 
                  and access to exclusive subscriber-only gatherings.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
  
  // Render modal using Portal
  return modalRoot ? createPortal(modalContent, modalRoot) : null;
};

export default SubscriptionRequiredModal;